/**
 * Roller ve yetkiler ekranı.
 *
 * İki parça:
 *   1. **Rol kartları** — hangi rol var, kaç kullanıcısı var, kaç izni var
 *   2. **İzin matrisi** — satır = alan, kolon = Görme · Düzenleme · Silme
 *
 * Matrisin iki özelliği bilinçli:
 *
 * - **Anlamsız kutu GÖSTERİLMEZ.** Fatura silinemez (7 yıl saklama
 *   zorunluluğu), o yüzden o hücre boş çizilir. Tıklanabilir ama işlemeyen bir
 *   kutu, birinin bir gün "neden çalışmıyor" diye sormasına yol açar.
 *
 * - **Veremeyeceğin izin KİLİTLİ görünür.** Sunucu kuralı: kimse kendisinde
 *   olmayan yetkiyi dağıtamaz. Panel bunu önden gösterir ki kullanıcı
 *   kaydetmeye çalışıp hata almasın. Kilidin kendisi güvenlik değil —
 *   sunucu her hâlükârda reddeder (src/panel/guard.ts).
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Pencil, Plus, ShieldCheck, Trash2, TriangleAlert, Users } from 'lucide-react';
import { api, type IzinKatalogu, type Rol } from '../lib/api';
import { Kabuk } from '../components/Kabuk';
import { Kart, KartGovdesi } from '../components/ui/Kart';
import { Dugme } from '../components/ui/Dugme';
import { Girdi } from '../components/ui/Girdi';
import { Pencere } from '../components/ui/Pencere';
import { BosDurum, HataKutusu, Yukleniyor } from '../components/Durumlar';
import { basariBildir, hataBildir, onayIste } from '../lib/bildirim';
import { cn } from '../lib/cn';

// ---------------------------------------------------------------------------
// İzin matrisi
// ---------------------------------------------------------------------------

interface MatrisProps {
  katalog: IzinKatalogu;
  secili: Set<string>;
  onDegistir: (secili: Set<string>) => void;
  /** Salt okunur gösterim (Süper Yönetici rolü gibi). */
  kilitli?: boolean;
}

function IzinMatrisi({ katalog, secili, onDegistir, kilitli = false }: MatrisProps) {
  const verilebilir = useMemo(
    () => new Set(katalog.canGrantAll ? undefined : katalog.myPermissions),
    [katalog]
  );

  /** Bu izni bu kullanıcı verebilir mi? */
  const verebilirMi = (izin: string): boolean => katalog.canGrantAll || verilebilir.has(izin);

  const tumIzinler = katalog.matrix.flatMap((s) =>
    s.hucreler.filter((h) => h.izin !== null && verebilirMi(h.izin)).map((h) => h.izin as string)
  );
  const hepsiSecili = tumIzinler.length > 0 && tumIzinler.every((i) => secili.has(i));

  const tekDegistir = (izin: string, acik: boolean) => {
    const yeni = new Set(secili);
    if (acik) yeni.add(izin);
    else yeni.delete(izin);
    onDegistir(yeni);
  };

  const hepsiniDegistir = (acik: boolean) => {
    const yeni = new Set(secili);
    for (const izin of tumIzinler) {
      if (acik) yeni.add(izin);
      else yeni.delete(izin);
    }
    onDegistir(yeni);
  };

  return (
    <div className="kap flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="ust-etiket">Yetkiler</span>
        {!kilitli && (
          <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--ink-muted)]">
            <input
              type="checkbox"
              checked={hepsiSecili}
              onChange={(olay) => hepsiniDegistir(olay.target.checked)}
              className="size-4 accent-[var(--accent)]"
            />
            Tümünü seç
          </label>
        )}
      </div>

      {/* Geniş kapta tablo, dar kapta alan alan kart — tek bileşen, iki görünüm. */}
      <div className="yatay-kaydir hidden @2xl:block">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-[var(--line)] bg-[var(--surface-sunken)]">
              <th className="ust-etiket px-3 py-2 font-semibold">Alan</th>
              {katalog.actions.map((eylem) => (
                <th key={eylem.key} className="ust-etiket px-3 py-2 text-center font-semibold">
                  {eylem.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {katalog.matrix.map((satir) => (
              <tr key={satir.alan} className="border-b border-[var(--line)] last:border-0">
                <td className="px-3 py-2.5">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-[var(--ink)]">{satir.etiket}</span>
                    <span className="text-xs text-[var(--ink-faint)]">{satir.aciklama}</span>
                  </div>
                </td>
                {satir.hucreler.map((hucre) => (
                  <td key={hucre.eylem} className="px-3 py-2.5 text-center">
                    {!hucre.varMi || hucre.izin === null ? (
                      // Anlamsız kutu: çizgi. "Bu alanda bu eylem yok" demek.
                      <span className="text-[var(--ink-faint)]" title="Bu alanda böyle bir yetki yok">
                        —
                      </span>
                    ) : !verebilirMi(hucre.izin) ? (
                      <span
                        className="inline-flex text-[var(--ink-faint)]"
                        title="Bu yetki sizde olmadığı için başkasına veremezsiniz"
                      >
                        <Lock size={14} aria-label="veremezsiniz" />
                      </span>
                    ) : (
                      <label
                        className="inline-flex cursor-pointer items-center justify-center"
                        title={hucre.not ?? undefined}
                      >
                        <input
                          type="checkbox"
                          disabled={kilitli}
                          checked={secili.has(hucre.izin)}
                          onChange={(olay) => tekDegistir(hucre.izin as string, olay.target.checked)}
                          className={cn(
                            'size-4 accent-[var(--accent)] disabled:opacity-50',
                            hucre.tehlikeli && 'accent-[var(--state-bad)]'
                          )}
                        />
                        <span className="sr-only">
                          {satir.etiket} — {hucre.eylem}
                        </span>
                      </label>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dar kap: her alan bir kart */}
      <ul className="flex flex-col gap-2 @2xl:hidden">
        {katalog.matrix.map((satir) => (
          <li key={satir.alan} className="rounded-[var(--radius-md)] border border-[var(--line)] p-3">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-[var(--ink)]">{satir.etiket}</span>
              <span className="text-xs text-[var(--ink-faint)]">{satir.aciklama}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-3 border-t border-[var(--line)] pt-3">
              {satir.hucreler.map((hucre) => {
                const eylemAdi = katalog.actions.find((a) => a.key === hucre.eylem)?.label ?? hucre.eylem;
                if (!hucre.varMi || hucre.izin === null) {
                  return (
                    <span key={hucre.eylem} className="text-xs text-[var(--ink-faint)] line-through">
                      {eylemAdi}
                    </span>
                  );
                }
                if (!verebilirMi(hucre.izin)) {
                  return (
                    <span
                      key={hucre.eylem}
                      className="inline-flex items-center gap-1 text-xs text-[var(--ink-faint)]"
                    >
                      <Lock size={11} aria-hidden="true" />
                      {eylemAdi}
                    </span>
                  );
                }
                return (
                  <label
                    key={hucre.eylem}
                    className="flex cursor-pointer items-center gap-1.5 text-xs text-[var(--ink)]"
                  >
                    <input
                      type="checkbox"
                      disabled={kilitli}
                      checked={secili.has(hucre.izin)}
                      onChange={(olay) => tekDegistir(hucre.izin as string, olay.target.checked)}
                      className="size-4 accent-[var(--accent)]"
                    />
                    {eylemAdi}
                  </label>
                );
              })}
            </div>
          </li>
        ))}
      </ul>

      <p className="text-xs text-[var(--ink-faint)]">
        <Lock size={11} className="mr-1 inline" aria-hidden="true" />
        kilitli yetkiler sizde olmadığı için verilemez. “—” işaretli hücreler o alanda anlamsızdır (örnek:
        fatura silinemez, 7 yıl saklama zorunluluğu).
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rol düzenleme paneli
// ---------------------------------------------------------------------------

interface DuzenlemeProps {
  katalog: IzinKatalogu;
  rol: Rol | null;
  bekliyor: boolean;
  onKaydet: (govde: { name: string; description: string; permissions: string[] }) => void;
  onKapat: () => void;
}

function RolDuzenleme({ katalog, rol, bekliyor, onKaydet, onKapat }: DuzenlemeProps) {
  const [ad, setAd] = useState(rol?.name ?? '');
  const [aciklama, setAciklama] = useState(rol?.description ?? '');
  const [secili, setSecili] = useState<Set<string>>(new Set(rol?.permissions ?? []));

  // Farklı bir rol düzenlemeye geçilince form o role dönsün.
  useEffect(() => {
    setAd(rol?.name ?? '');
    setAciklama(rol?.description ?? '');
    setSecili(new Set(rol?.permissions ?? []));
  }, [rol]);

  const kilitli = rol !== null && !rol.editable;

  const kaydedilebilir = !kilitli && ad.trim().length >= 2;
  const toplamYetki = katalog.matrix.reduce((t, s) => t + s.hucreler.filter((h) => h.varMi).length, 0);

  return (
    <Pencere
      acik
      genis
      baslik={rol ? `Rolü düzenle: ${rol.name}` : 'Yeni rol'}
      aciklama={
        kilitli
          ? 'Bu rol korumalıdır — izinleri değiştirilemez'
          : 'Rolün adını verin ve yetkilerini işaretleyin'
      }
      basliksag={
        rol?.systemKey ? (
          <span className="rounded-full bg-[var(--state-info-soft)] px-2 py-0.5 text-xs font-medium text-[var(--state-info)]">
            sistem rolü
          </span>
        ) : undefined
      }
      onKapat={onKapat}
      altCubuk={
        kilitli ? (
          <Dugme gorunum="ikincil" onClick={onKapat}>
            Kapat
          </Dugme>
        ) : (
          <>
            <Dugme
              gorunum="birincil"
              bekliyor={bekliyor}
              disabled={!kaydedilebilir}
              onClick={() =>
                onKaydet({ name: ad.trim(), description: aciklama.trim(), permissions: [...secili] })
              }
            >
              {rol ? 'Değişiklikleri kaydet' : 'Rolü oluştur'}
            </Dugme>
            <Dugme gorunum="ikincil" onClick={onKapat}>
              Vazgeç
            </Dugme>
            <span className="rakam ml-auto text-xs text-[var(--ink-faint)]">
              {secili.size} / {toplamYetki} yetki seçili
            </span>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {kilitli && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--state-wait)] bg-[var(--state-wait-soft)] p-3"
          >
            <span className="-mt-px shrink-0 text-[var(--state-wait)]" aria-hidden="true">
              <TriangleAlert size={16} strokeWidth={2} />
            </span>
            <p className="max-w-[65ch] text-sm text-[var(--ink)]">
              Süper Yönetici rolünün izinleri kısıtlanamaz. Aksi hâlde tek süper yönetici kendini dışarıda
              bırakıp sistemi kilitleyebilirdi. Birini kısıtlamak için başka bir role atayın.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2">
          <Girdi
            etiket="Rol adı"
            value={ad}
            onChange={(o) => setAd(o.target.value)}
            disabled={kilitli}
            required
            minLength={2}
            placeholder="Muhasebeci"
          />
          <Girdi
            etiket="Açıklama"
            value={aciklama}
            onChange={(o) => setAciklama(o.target.value)}
            disabled={kilitli}
            placeholder="Faturaları görür, belge indirir"
            ipucu="Bu rolün ne işe yaradığını yazın — sonradan hatırlamak kolay olsun"
          />
        </div>

        <div className="border-t border-[var(--line)] pt-4">
          <IzinMatrisi katalog={katalog} secili={secili} onDegistir={setSecili} kilitli={kilitli} />
        </div>
      </div>
    </Pencere>
  );
}

// ---------------------------------------------------------------------------
// Ekran
// ---------------------------------------------------------------------------

export function Roller() {
  const sorguIstemcisi = useQueryClient();
  const [duzenlenen, setDuzenlenen] = useState<Rol | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);

  const roller = useQuery({ queryKey: ['roller'], queryFn: api.roller });
  const katalog = useQuery({ queryKey: ['izin-katalogu'], queryFn: api.izinKatalogu });

  const tazele = () => {
    void sorguIstemcisi.invalidateQueries({ queryKey: ['roller'] });
    void sorguIstemcisi.invalidateQueries({ queryKey: ['kullanicilar'] });
  };

  const kapat = () => {
    setDuzenlenen(null);
    setYeniAcik(false);
  };

  const ekle = useMutation({
    mutationFn: api.rolEkle,
    onSuccess: (sonuc) => {
      basariBildir(sonuc.message, 'Rol oluşturuldu');
      tazele();
      kapat();
    },
    onError: hataBildir,
  });

  const guncelle = useMutation({
    mutationFn: ({ id, yama }: { id: string; yama: Parameters<typeof api.rolGuncelle>[1] }) =>
      api.rolGuncelle(id, yama),
    onSuccess: (sonuc) => {
      basariBildir(sonuc.message);
      tazele();
      kapat();
    },
    onError: hataBildir,
  });

  const sil = useMutation({
    mutationFn: api.rolSil,
    onSuccess: (sonuc) => {
      basariBildir(sonuc.message);
      tazele();
    },
    onError: hataBildir,
  });

  const silmeyiSor = async (rol: Rol) => {
    const onay = await onayIste({
      baslik: 'Rol silinsin mi?',
      mesaj:
        `"${rol.name}" rolü kalıcı olarak silinecek. ` +
        'Bu rolde kullanıcı varsa işlem reddedilir; önce onları başka bir role taşımanız gerekir.',
      onayMetni: 'Rolü sil',
      tehlikeli: true,
    });
    if (onay) sil.mutate(rol.id);
  };

  const yukleniyor = roller.isPending || katalog.isPending;
  const hata = roller.error ?? katalog.error;

  return (
    <Kabuk
      baslik="Roller ve yetkiler"
      altBaslik={
        roller.data
          ? `${roller.data.items.length} rol · ${roller.data.totalPermissions} yetki tanımlı`
          : 'Kim neyi görebilir, neyi değiştirebilir'
      }
      eylemler={
        roller.data?.canEdit ? (
          <Dugme
            gorunum={yeniAcik ? 'ikincil' : 'birincil'}
            ikon={<Plus size={15} />}
            onClick={() => {
              setDuzenlenen(null);
              setYeniAcik((a) => !a);
            }}
          >
            {yeniAcik ? 'Vazgeç' : 'Yeni rol'}
          </Dugme>
        ) : undefined
      }
    >
      {hata != null && (
        <HataKutusu
          hata={hata}
          tekrarDene={() => {
            void roller.refetch();
            void katalog.refetch();
          }}
        />
      )}

      {(yeniAcik || duzenlenen) && katalog.data && (
        <RolDuzenleme
          katalog={katalog.data}
          rol={duzenlenen}
          bekliyor={ekle.isPending || guncelle.isPending}
          onKapat={kapat}
          onKaydet={(govde) => {
            if (duzenlenen) {
              guncelle.mutate({
                id: duzenlenen.id,
                yama: {
                  name: govde.name,
                  description: govde.description,
                  permissions: govde.permissions,
                },
              });
            } else {
              ekle.mutate({
                name: govde.name,
                ...(govde.description ? { description: govde.description } : {}),
                permissions: govde.permissions,
              });
            }
          }}
        />
      )}

      {yukleniyor ? (
        <Kart>
          <Yukleniyor satir={4} />
        </Kart>
      ) : !roller.data || roller.data.items.length === 0 ? (
        <Kart>
          <BosDurum
            ikon={<ShieldCheck size={32} strokeWidth={1.5} />}
            baslik="Henüz rol yok"
            aciklama="Sistem rolleri kurulumda oluşturulur. Bir şeyler ters gitmiş olabilir."
          />
        </Kart>
      ) : (
        <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">
          {roller.data.items.map((rol) => (
            <Kart key={rol.id}>
              <KartGovdesi className="flex h-full flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="truncate text-base font-semibold text-[var(--ink)]">{rol.name}</span>
                    <span
                      className={cn(
                        'w-fit rounded-full px-2 py-0.5 text-xs font-medium',
                        rol.systemKey
                          ? 'bg-[var(--state-info-soft)] text-[var(--state-info)]'
                          : 'bg-[var(--state-done-soft)] text-[var(--state-done)]'
                      )}
                    >
                      {rol.systemKey ? 'sistem rolü' : 'özel rol'}
                    </span>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 text-sm text-[var(--ink-muted)]">
                    <Users size={14} aria-hidden="true" />
                    <span className="rakam">{rol.userCount}</span>
                  </span>
                </div>

                {rol.description && <p className="text-sm text-[var(--ink-muted)]">{rol.description}</p>}

                {/* İzin doluluk oranı — rolün "ne kadar geniş" olduğu bir bakışta. */}
                <div className="mt-auto flex flex-col gap-1.5 border-t border-[var(--line)] pt-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs text-[var(--ink-muted)]">Yetki</span>
                    <span className="rakam text-xs text-[var(--ink)]">
                      {rol.permissions.length} / {roller.data.totalPermissions}
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-[var(--surface-sunken)]">
                    <div
                      className={cn(
                        'h-full rounded-full',
                        rol.systemKey === 'SUPER_ADMIN' ? 'bg-[var(--state-bad)]' : 'bg-[var(--accent)]'
                      )}
                      style={{
                        width: `${Math.max(
                          2,
                          Math.round((rol.permissions.length / roller.data.totalPermissions) * 100)
                        )}%`,
                      }}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Dugme
                    boy="sm"
                    ikon={<Pencil size={13} />}
                    onClick={() => {
                      setYeniAcik(false);
                      setDuzenlenen(rol);
                    }}
                  >
                    {rol.editable && roller.data.canEdit ? 'Düzenle' : 'Görüntüle'}
                  </Dugme>
                  {roller.data.canDelete && rol.deletable && (
                    <Dugme
                      boy="sm"
                      gorunum="tehlike"
                      ikon={<Trash2 size={13} />}
                      bekliyor={sil.isPending}
                      onClick={() => void silmeyiSor(rol)}
                    >
                      Sil
                    </Dugme>
                  )}
                </div>
              </KartGovdesi>
            </Kart>
          ))}
        </div>
      )}
    </Kabuk>
  );
}
